/**
 * The decision the BFF refuses to take: what a structural edit means.
 *
 * A survey with answers cannot have its questionnaire rewritten in place — the answers point at
 * the text that was asked, and changing it under them rewrites history silently (R1/R2). So the
 * save comes back `409` and lands here, where the only question left is whether the people who
 * already answered should be asked again (R3).
 *
 * Both options say **how many people** they affect, because that is the whole difference between
 * them and the words alone do not carry it: "corrigir" and "perguntar de novo" sound like a matter
 * of taste until the number is on screen.
 *
 * ## "Perguntar de novo" is offered on an exit questionnaire too
 *
 * It used to be refused there: the exit questionnaire was asked at every exit, so `reask` had
 * nothing to put back in any queue and the BFF answered `409`. Since R33 it is asked once per
 * person per version, exactly like every other survey — and this button is now precisely how
 * somebody who already answered gets asked again.
 *
 * What does change is the **consequence**, because an exit questionnaire has no queue to go back
 * to (R34): it is never delivered on its own, and the only moment it is asked is somebody turning
 * the feature off. So there the sentence names that moment instead of a queue — "volta a ser
 * perguntada na próxima vez que desligar" — rather than promising a delivery that never happens.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { formatCount } from '../../../Utils/surveyFormat';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
`;

const leadStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const optionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const optionHeadStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const optionTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const spacerStyle = css`
  flex: 1;
`;

const optionBodyStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const consequenceStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`;

export interface SurveyVersionDialogProps {
  /** How many responses the version under edit already has. */
  responseCount: number;
  /**
   * Whether this is an exit questionnaire (`trigger: ON_OPT_OUT`).
   *
   * Both options stay on offer either way — only the sentence describing what each one costs
   * changes, because an exit questionnaire is never in a queue: it is asked at the moment someone
   * turns the feature off, and nowhere else (R34).
   */
  isExitQuestionnaire?: boolean;
  isWorking: boolean;
  onChoose: (reask: boolean) => void;
  onClose: () => void;
}

export const SurveyVersionDialog = ({
  responseCount,
  isExitQuestionnaire = false,
  isWorking,
  onChoose,
  onClose,
}: SurveyVersionDialogProps): JSX.Element => {
  /*
    A frase inteira concorda, não só o substantivo. A versão anterior escolhia "pessoa"/"pessoas"
    e deixava verbo e pronome fixos no plural — "1 pessoa já responderam", "Afeta 1 pessoa: elas
    não voltam" —, e a tela com uma resposta só é justamente a que aparece no primeiro teste de
    qualquer pesquisa, que é onde ela desmoralizava o resto do texto.
  */
  const isOne = responseCount === 1;
  const people = isOne ? '1 pessoa' : `${formatCount(responseCount)} pessoas`;
  const answered = isOne
    ? '1 pessoa já respondeu esta versão.'
    : `${formatCount(responseCount)} pessoas já responderam esta versão.`;

  /*
    The same two decisions, said in the terms of where the questionnaire is actually asked.

    An exit questionnaire has no queue (R34): it is never delivered on its own, and the only
    moment it reaches anybody is the opt-out modal. "Volta para a fila" there would describe a
    delivery that does not exist — and it is the sentence somebody reads before deciding.
  */
  const stays = isExitQuestionnaire
    ? isOne
      ? ['ela ', ' é perguntada de novo quando desligar a funcionalidade outra vez.']
      : ['elas ', ' são perguntadas de novo quando desligarem a funcionalidade outra vez.']
    : isOne
      ? ['ela ', ' volta para a fila e não vê a pesquisa de novo.']
      : ['elas ', ' voltam para a fila e não veem a pesquisa de novo.'];

  const returns = isExitQuestionnaire
    ? isOne
      ? 'ela volta a ser perguntada na próxima vez que desligar a funcionalidade.'
      : 'todas voltam a ser perguntadas na próxima vez que desligarem a funcionalidade.'
    : isOne
      ? 'ela volta para a fila e recebe a pesquisa outra vez.'
      : 'todas voltam para a fila e recebem a pesquisa outra vez.';

  const lead = isExitQuestionnaire
    ? 'o que resta decidir é se essas pessoas voltam a ser perguntadas quando desligarem a funcionalidade de novo.'
    : 'o que resta decidir é se essas pessoas voltam para a fila.';

  const rest = isExitQuestionnaire
    ? 'Quem ainda não respondeu vê o texto corrigido ao sair.'
    : 'Quem ainda não respondeu vê o texto corrigido.';

  return (
    <UTModal
      open
      position='center'
      maxWidth='560px'
      hasButtonClose
      modalContentId='survey-version-dialog'
      closeButtonId='survey-version-dialog-close'
      title='Esta pesquisa já tem respostas'
      description='Mudar as perguntas exige uma nova versão. As respostas atuais continuam ligadas ao texto que foi perguntado.'
      content={
        <div className={contentStyle}>
          <p className={leadStyle}>
            {answered} A nova versão nasce com uma cópia do questionário e recebe as suas mudanças;{' '}
            {lead}
          </p>

          <div className={optionStyle}>
            <div className={optionHeadStyle}>
              <span className={optionTitleStyle}>Só corrigir</span>
              <div className={spacerStyle} />
              <UTButton
                fitContent
                variant='secondary'
                size='small'
                id='survey-version-fix'
                text='Só corrigir'
                loading={isWorking}
                onClick={() => {
                  onChoose(false);
                }}
              />
            </div>
            <p className={optionBodyStyle}>
              Para ajustes que não mudam o sentido da pergunta — um typo, uma vírgula, um rótulo
              mais claro.
            </p>
            <span className={consequenceStyle}>
              Afeta {people}: {stays[0]}
              <strong>não</strong>
              {stays[1]} {rest}
            </span>
          </div>

          <div className={optionStyle}>
            <div className={optionHeadStyle}>
              <span className={optionTitleStyle}>Perguntar de novo</span>
              <div className={spacerStyle} />
              <UTButton
                fitContent
                size='small'
                id='survey-version-reask'
                text='Perguntar de novo'
                loading={isWorking}
                onClick={() => {
                  onChoose(true);
                }}
              />
            </div>
            <p className={optionBodyStyle}>
              Para quando a pergunta passou a querer dizer outra coisa, e a resposta antiga não
              responde mais a nova pergunta.
            </p>
            <span className={consequenceStyle}>
              Afeta {people}: {returns} As respostas da versão anterior continuam guardadas, e os
              resultados das duas versões não se misturam.
            </span>
          </div>

          <div className={footerStyle}>
            <UTButton
              fitContent
              variant='tertiary'
              id='survey-version-cancel'
              text='Cancelar'
              onClick={onClose}
            />
          </div>
        </div>
      }
      onClose={onClose}
    />
  );
};
